import { AcOnlinetestModel, OnlinetestTypes } from 'projects/core/src/lib/models/ac-onlinetest.model';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Component, Input, OnInit, Output, EventEmitter, OnChanges, ViewChild, OnDestroy } from '@angular/core';
import { AcModel } from 'projects/core/src/lib/models/ac.model';
import { PartnerModel } from 'projects/core/src/lib/models/partner.model';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { CandidateModel } from 'projects/core/src/lib/models/candidate.model';
import { ResourceModel } from 'projects/core/src/lib/models/resource.model';
import { faDownload, faEnvelope, faTrash, faUpload } from '@fortawesome/pro-light-svg-icons';
import * as FileSaver from 'file-saver';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';
import { Subscription } from 'rxjs';
import { OnlinetestModel } from 'projects/core/src/lib/models/onlinetest.model';
import { CandidateAcOnlinetestModel } from 'projects/core/src/lib/models/candidate-ac-onlinetest.model';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { TranslocoService } from '@ngneat/transloco';
import { ONLINETEST_TYPE_NAMES } from 'projects/core/src/lib/constants';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { CANDIDATES_RIGHTS, REPORTS_RIGHTS } from 'projects/admin/src/app/shared/constants';

const FILE_TYPE = 'onlinetest.report';
const NO_API = 'no_api';

class CustomCandidateModel extends CandidateModel {
  url?: UntypedFormControl;
  user?: UntypedFormControl;
  password?: UntypedFormControl;
  comment?: UntypedFormControl;
  overrule?: UntypedFormControl;
}

@Component({
  selector: 'app-ac-onlinetests',
  templateUrl: './ac-onlinetests.component.html',
  styleUrls: ['./ac-onlinetests.component.scss']
})
export class AcOnlinetestsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() ac: AcModel = null;
  @Input('partners') all: Array<PartnerModel> = []; // tslint:disable-line:no-input-rename
  @Output() onlinetestsChange = new EventEmitter<AcModel>();
  @Output() setEdit = new EventEmitter<string>();

  @ViewChild('upload') uploadComponent: any;

  public inEdit = false;
  public editOnlinetest: AcOnlinetestModel = null;
  public uploadCandidate: CandidateAcOnlinetestModel = null;
  public typeNames: any = ONLINETEST_TYPE_NAMES;
  public submitting = false;
  public optionsPartners: Array<any> = [];
  public optionsTypes: Array<SelectItem> = [];
  public editCompetences: Array<CompetenceModel> = [];
  public hiddenCompetences: Array<CompetenceModel> = [];
  public candidates: Array<CustomCandidateModel> = [];
  public uploadOpened = false;

  public faTrash = faTrash;
  public faDownload = faDownload;
  public faUpload = faUpload;
  public faEnvelope = faEnvelope;

  public form: UntypedFormGroup = this.fb.group({
    onlinetest: new UntypedFormControl(null),
    type: new UntypedFormControl(null, [Validators.required]),
    duration: new UntypedFormControl(null),
    rating_duration: new UntypedFormControl(null),
    unlock_days: new UntypedFormControl(null),
    generalized: new UntypedFormControl(true),
    login_url: new UntypedFormControl(null, [CustomValidators.url()]),
    login_user: new UntypedFormControl(null),
    login_password: new UntypedFormControl(null),
    comment: new UntypedFormControl(null),
  });

  private onlinetestSubscription: Subscription = null;
  private typeSubscription: Subscription = null;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.optionsTypes = FormHelper.getSelectList(ONLINETEST_TYPE_NAMES, null, this.transloco);
    this.typeSubscription = this.form.get('type').valueChanges.subscribe(() => {
      this.setFormHandling();
    });
    this.ui.setFocus();
  }

  ngOnDestroy(): void {
    if (this.typeSubscription !== null) {
      this.typeSubscription.unsubscribe();
    }
    if (this.onlinetestSubscription !== null) {
      this.onlinetestSubscription.unsubscribe();
    }
  }

  ngOnChanges(): void {
    this.candidates = this.ac.candidates.map((candidate: CandidateModel) => {
      const data = {...candidate, ...{
        url: new UntypedFormControl('', [CustomValidators.url()]),
        user: new UntypedFormControl(''),
        password: new UntypedFormControl(''),
        comment: new UntypedFormControl(''),
        overrule: new UntypedFormControl(''),
      }};
      return new CustomCandidateModel().deserialize(data);
    });
    this.buildOnlinetests();
  }

  onNew(): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.editOnlinetest = null;
      this.clearForm();
      this.onlinetestSubscription = this.form.get('onlinetest').valueChanges.subscribe((value: OnlinetestModel) => {
        if (value !== null) {
          this.editCompetences = value.competences;
          this.hiddenCompetences = [];
          this.setDisabling();
        } else {
          this.editCompetences = [];
          this.hiddenCompetences = [];
        }
      });
      this.startEdit();
    }
  }

  onEdit(onlinetest: AcOnlinetestModel): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      let generalized = this.ac.isGroupAc() && onlinetest.api_active !== true;
      this.editOnlinetest = onlinetest;
      this.buildOnlinetests();
      this.form.patchValue({
        onlinetest: this.getChildValue(onlinetest),
        type: onlinetest.type,
        duration: onlinetest.duration,
        unlock_days: onlinetest.unlock_days,
        rating_duration: onlinetest.rating_duration,
        login_url: onlinetest?.pivot?.misc?.login_url || '',
        login_user: onlinetest?.pivot?.misc?.login_user || '',
        login_password: onlinetest?.pivot?.misc?.login_password || '',
        comment: onlinetest?.pivot?.misc?.comment || '',
      });
      onlinetest.candidates.forEach((candidateData: any) => {
        const candidate = this.candidates.find(actual => actual.hashed_id === candidateData?.hashed_id);
        if (candidate) {
          candidate.url.setValue(candidateData.login_url);
          candidate.user.setValue(candidateData.login_user);
          candidate.password.setValue(candidateData.login_password);
          candidate.comment.setValue(candidateData.comment);
          candidate.overrule.setValue(candidateData['status'] === NO_API);
          if (
            candidateData.login_url !== onlinetest?.pivot?.misc?.login_url ||
            candidateData.login_user !== onlinetest?.pivot?.misc?.login_user ||
            candidateData.login_password !== onlinetest?.pivot?.misc?.login_password ||
            candidateData.comment !== onlinetest?.pivot?.misc?.comment
          ) {
            generalized = false;
          }
        }
      });
      this.form.get('generalized').setValue(generalized);
      this.editCompetences = this.editOnlinetest.competences;
      this.hiddenCompetences = this.editOnlinetest.hidden_competences;
      this.setDisabling();
      this.startEdit();
    }
  }

  onDelete(onlinetest: AcOnlinetestModel): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.confirmation.confirm({
        message: this.transloco.translate('ac_onlinetests.really_delete', { value: onlinetest.fullname }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteAcOnlinetest('acs', this.ac.id, onlinetest.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: onlinetest.fullname }), this.transloco.translate('common.deleted'));
              const index = this.ac.onlinetests.indexOf(onlinetest);
              if (index > -1) {
                this.ac.onlinetests.splice(index, 1);
              }
              this.buildOnlinetests();
              this.emitOnlintestsChanged();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  onCancel(): void {
    this.editOnlinetest = null;
    this.buildOnlinetests();
    this.stopEdit();
    this.ui.hideMessage();
  }

  onSubmit(): void {
    if (this.ac !== null && this.inEdit === true) {
      this.ui.hideMessage();
      if (this.form.valid === true) {
        this.submitting = true;
        this.api.saveAcOnlinetest('acs', this.ac.id, this.getPayload()).subscribe({
            next: (result: any) => {
              let name: string;
              if (this.editOnlinetest !== null) {
                this.editOnlinetest.deserialize(result.data);
                name = this.editOnlinetest.name;
              } else {
                const newOnlinetest = new AcOnlinetestModel().deserialize(result.data);
                this.ac.onlinetests.push(newOnlinetest);
                name = newOnlinetest.name;
              }
              this.ui.toastSuccess(
                this.transloco.translate('common.messages.saved', { value: name }),
                this.transloco.translate('common.saved')
              );
              this.emitOnlintestsChanged();
              this.buildOnlinetests();
              this.stopEdit();
            },
            error: (error: any) => {
              this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
              FormHelper.setBackendErrors(this.form, error.fielderrors);
            }
          })
          .add(() => {
            this.submitting = false;
          });
      } else {
        this.ui.showMessage(
          'error',
          this.transloco.translate('common.error_on_form_please_correct'),
          this.transloco.translate('common.error')
        );
        FormHelper.markFormAsDirty(this.form);
      }
    }
  }

  startEdit(): void {
    this.inEdit = true;
    this.setEdit.emit('onlinetests');
    this.setFormHandling();
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.clearForm();
    this.setEdit.emit(null);
    this.ui.setFocus();
    this.editCompetences = [];
    this.hiddenCompetences = [];
  }

  onUploadReport(candidate: CandidateAcOnlinetestModel): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      if (this.ac.is_ac === true) {
        this.uploadOpened = true;
        this.uploadCandidate = candidate;
      }
    }
  }

  onStartUpload(): void {
    if (this.ac.is_ac === true) {
      this.uploadComponent.upload();
    }
  }

  onUploadFiles(event: any): void {
    if (this.ac.is_ac === true) {
      this.submitting = true;
      this.api.uploadFiles(event.files, FILE_TYPE, this.uploadCandidate.id).subscribe({
        next: (result: any) => {
          if (result.data?.length > 0) {
            let message = this.transloco.translate('ac_onlinetests.reports_added', { number: event.files.length });
            result.data.forEach((resource: any) => {
              this.uploadComponent.clear();
              this.uploadCandidate.resources.push(new ResourceModel().deserialize(resource));
              this.uploadOpened = false;
              if (event.files.length === 1) {
                message = this.transloco.translate('ac_onlinetests.report_added', { value: resource.public_name });
              }
            });
            this.ui.toastSuccess(message, this.transloco.translate('common.added'));
            this.uploadCandidate = null;
          }
        }
      }).add(() => {
        this.submitting = false;
      });
    }
  }

  onDeleteReport(candidate: CandidateAcOnlinetestModel, resource: ResourceModel): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: resource.public_name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api
            .deleteFile(
              resource.id,
              FILE_TYPE,
              candidate.id,
            )
            .subscribe({
              next: () => {
                this.ui.toastSuccess(
                  this.transloco.translate('common.messages.deleted', { value: resource.public_name }),
                  this.transloco.translate('common.deleted')
                );
                candidate.resources.splice(candidate.resources.indexOf(resource), 1);
              },
              error: (error: any) => {
                this.ui.toastError(this.transloco.translate(error.message));
              }
            });
        }
      });
    }
  }

  onDownloadReport(candidate: CandidateAcOnlinetestModel, resource: ResourceModel): void {
    if (this.auth.hasRight('admin.ac.edit')) {
      if (candidate) {
        this.api
          .downloadFile(
            resource.id,
            FILE_TYPE,
            candidate.id,
          )
          .subscribe({
            next: (response: any) => {
              const blob = new Blob([response]);
              FileSaver.saveAs(blob, resource.public_name);
            },
            error: () => {
              this.ui.toastError(this.transloco.translate('common.file_not_found'));
            }
          });
      }
    }
  }

  onSendReminder(candidateAcOnlinetest: CandidateAcOnlinetestModel): void {
    if (candidateAcOnlinetest && this.ac.is_ac === true) {
      this.confirmation.confirm({
        message: this.transloco.translate('ac_onlinetests.really_send_reminder'),
        header: this.transloco.translate('common.labels.send_reminder'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api
            .sendOnlinetestReminder(
              this.ac.id,
              candidateAcOnlinetest.id,
            )
            .subscribe({
              next: (response: any) => {
                this.ui.toastSuccess(this.transloco.translate('ac_onlinetests.reminder_sended'), this.transloco.translate('common.labels.sended'));
              },
              error: () => {
                this.ui.toastError(this.transloco.translate('ac_onlinetests.reminder_error'));
              }
            });
        }
      });
    }
  }

  canSeeCandidates(): boolean {
    return this.auth.hasOneRight(CANDIDATES_RIGHTS);
  }

  canSeeReports(): boolean {
    return this.auth.hasOneRight(REPORTS_RIGHTS);
  }

  setHiddenCompetences(competences: Array<CompetenceModel>): void {
    this.hiddenCompetences = competences;
  }

  public isBefore(): boolean {
    return this.inEdit === true && this.form.get('type').value === OnlinetestTypes.Before;
  }

  public isDuringAc(): boolean {
    return this.inEdit === true && this.form.get('type').value === OnlinetestTypes.Normal;
  }

  public isGeneralized(): boolean {
    return this.inEdit === true && this.form.get('generalized').value === true;
  }

  public getCandidateName(hashed_id: string): string {
    return this.ac?.candidates?.find(candidate => candidate.hashed_id === hashed_id)?.fullname || '';
  }

  private getPayload(): any {
    const payload: any = {
      onlinetest: (this.editOnlinetest !== null) ? this.editOnlinetest.id : this.form.get('onlinetest').value.id,
      generalized: this.form.get('generalized').value,
      type: this.form.get('type').value,
      hidden_competences: this.hiddenCompetences.map((competence: CompetenceModel) => {
        return competence.id;
      }),
      candidates: {},
      misc: {
        login_url: this.form.get('login_url').value,
        login_user: this.form.get('login_user').value,
        login_password: this.form.get('login_password').value,
        comment: this.form.get('comment').value,
      }
    };
    if (this.form.get('type').value === OnlinetestTypes.Before) {
      payload.unlock_days = this.form.get('unlock_days').value;
    } else {
      payload.duration =  this.form.get('duration').value;
      payload.rating_duration = this.form.get('rating_duration').value;
    }
    this.candidates.forEach((candidate: CustomCandidateModel) => {
      payload.candidates[candidate.hashed_id] = {
        login_url: candidate.url.value,
        login_user: candidate.user.value,
        login_password: candidate.password.value,
        comment: candidate.comment.value,
        overrule: candidate.overrule.value,
      };
    });
    return payload;
  }

  private buildOnlinetests(): void {
    this.optionsPartners = [];
    const acIds = this.ac.onlinetests.map((onlinetest: OnlinetestModel) => {
      return onlinetest.id;
    });
    this.all?.forEach((actualPartner: PartnerModel) => {
      const partner = new PartnerModel().deserialize({
        ...actualPartner,
        onlinetests: actualPartner.onlinetests.filter((onlinetest: OnlinetestModel) => {
          return this.editOnlinetest?.id === onlinetest.id ||
            (onlinetest.for_ac === true && acIds.indexOf(onlinetest.id) === -1);
        })
      });
      if (partner.onlinetests.length > 0) {
        this.optionsPartners.push(partner);
      }
    });
  }

  private emitOnlintestsChanged(): void {
    this.ac = new AcModel().deserialize({...this.ac});
    this.onlinetestsChange.emit(this.ac);
  }

  private setFormHandling(): void {
    if (this.editOnlinetest === null) {
      this.form.get('onlinetest').setValidators([Validators.required]);
      this.form.get('onlinetest').enable();
    } else {
      this.form.get('onlinetest').clearValidators();
      this.form.get('onlinetest').disable();
    }
    this.form.get('onlinetest').updateValueAndValidity();
    const duration = this.form.get('duration');
    const ratingDuration = this.form.get('rating_duration');
    const unlockDays = this.form.get('unlock_days');
    if (this.form.get('type').value === OnlinetestTypes.Before) {
      duration.clearValidators();
      duration.setValue('');
      ratingDuration.setValue('');
      unlockDays.setValidators([Validators.required, Validators.min(1), Validators.max(60)]);
    } else {
      duration.setValidators([Validators.required]);
      unlockDays.clearValidators();
      unlockDays.setValue('');
    }
  }

  private clearForm(): void {
    if (this.form !== null) {
      this.form.reset();
      this.form.patchValue({
        type: '',
        duration: '',
        unlock_days: '',
        rating_duration: '',
        generalized: false,
        login_url: '',
        login_user: '',
        login_password: '',
        comment: '',
      });
      this.candidates.forEach((candidate: CustomCandidateModel) => {
        candidate.url.setValue('');
        candidate.user.setValue('');
        candidate.password.setValue('');
        candidate.comment.setValue('');
        candidate.overrule.setValue(false);
      });
    }
    this.setDisabling();
  }

  private getChildValue(child: AcOnlinetestModel): OnlinetestModel {
    let result = null;
    this.optionsPartners.forEach((partner: PartnerModel) => {
      const found = partner.onlinetests.find(onlinetest => onlinetest.id === child.pivot.onlinetest_id);
      if (found) {
        result = found;
      }
    });
    return result;
  }

  public onToggleOverrule(candidate: CustomCandidateModel): void {
    candidate.overrule.setValue(!candidate.overrule.value);
    this.setDisabling();
  }

  private setDisabling(): void {
    this.candidates.forEach((candidate: CustomCandidateModel) => {
      if (this.form.get('onlinetest').value && this.form.get('onlinetest').value.api_active !== true) {
        candidate.url.enable();
        candidate.user.enable();
        candidate.password.enable();
      } else {
        if (candidate.overrule.value !== true) {
          candidate.url.disable();
          candidate.user.disable();
          candidate.password.disable();
        } else {
          candidate.url.enable();
          candidate.user.enable();
          candidate.password.enable();
        }
      }
    });
  }

}
